import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { pdfjs } from 'react-pdf';
import './style.scss';
import DefaultButton from 'components/Utils/NewDefaultButton';
import MiscUtils from 'services/MiscUtils';
import ProgressMessage from 'components/Utils/ProgressMessage';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppSelector } from 'careand-redux/store';
import fontIncreaseSvg from 'assets/img/icons/font-increase.svg';
import fontDecreaseSvg from 'assets/img/icons/font-decrease.svg';
import deleteSvg from 'assets/img/icons/delete.svg';
import Icon from 'components/Utils/NewIcon';
import 'fabric-history';
import StaffUtils from 'services/StaffUtils';
import PatientUtils from 'services/PatientUtils';
import { getBillingNumberLabel, getCollegeLabel, getHealthCardLabel } from 'services/portalData';
import PerformanceUtils from 'services/PerformanceUtils';
import CPPUtils from 'services/CPPUtils';
import { HealthRecordsShortcuts } from 'services/DocumentTemplateUtils';
import DefaultTooltip from 'components/Utils/DefaultTooltip';
import ConfirmationButton from '../../../Utils/NewDefaultButton/ConfirmationButton';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const fabric = window.fabric;
const DEFAULT_OBJECT = {};

/**
 * @params {any} props
 */
const SignDocument = (props) => {
  const { document, patient, onClose } = props;

  const dispatch = useAppDispatch();
  const authentication = useAppSelector((state) => state.authentication);
  const clinics = useAppSelector((state) => state.resource.clinics);

  const physicianId = props.physician?.id || authentication.user.id;
  const patientId = props.patient?.id;
  const clinicId = props.physician?.clinic_id;

  const physician = useAppSelector((state) => state.physicians.byId[physicianId]);

  const patientHealthRecords =
    useAppSelector((state) => state.healthRecords.byId[patientId]) || DEFAULT_OBJECT;

  const originalUrl = props.url;
  const [signatureData, setSignatureData] = useState();
  const [url, setUrl] = useState(originalUrl);
  const [exportData, setExportData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [width, setWidth] = useState(0);
  const [maxPages, setMaxPages] = useState(undefined);
  const [totalPages, setTotalPages] = useState(undefined);

  useEffect(() => {
    if (patientId) dispatch(api.HealthRecords.getCPP({ patientId }));
  }, [dispatch, patientId]);

  const clinic = useMemo(() => {
    return clinics.find((clinic) => clinic.id === clinicId);
  }, [clinics, clinicId]);

  const documentId = document?.id;

  useEffect(() => {
    if (!originalUrl) {
      dispatch(api.Documents.getDocumentFile({ documentId })).then((result) => {
        if (result) {
          setUrl(MiscUtils.getBlobUrl(result));
        }
      });
    }
  }, [documentId, originalUrl, dispatch]);

  useEffect(() => {
    async function loadSignature() {
      const result = await dispatch(api.Physicians.getPhysicianSignature({ id: physicianId }));
      if (result) {
        const reader = new window.FileReader();
        reader.readAsDataURL(result);
        reader.onloadend = () => {
          setSignatureData(reader.result);
        };
      }
    }

    if (!signatureData) loadSignature();
  }, [dispatch, physicianId, signatureData]);

  const macroData = useMemo(() => {
    return CPPUtils.getMacroData(patientHealthRecords);
  }, [patientHealthRecords]);

  async function saveChanges() {
    const { canvas, pageSizes } = exportData;

    setSaving(true);
    canvas.clone(async (newCanvas) => {
      let originalObjects = newCanvas.getObjects();

      const pageSizesCopy = PerformanceUtils.clone(pageSizes);

      const pages = originalObjects.slice(0, pageSizesCopy.length);
      const frames = originalObjects.slice(pageSizesCopy.length, pageSizesCopy.length * 2);
      const otherObjects = originalObjects.slice(pageSizesCopy.length * 2);

      let top = 0;

      pages.forEach((page, pageIndex) => {
        pageSizesCopy[pageIndex].top = top;
        top += pageSizesCopy[pageIndex].height;
        otherObjects.forEach((object) => {
          if (page.intersectsWithObject(object)) {
            pageSizesCopy[pageIndex].shouldExport = true;
            pageSizesCopy[pageIndex].index = pageIndex;
          }
        });
      });

      pages.forEach((object) => newCanvas.remove(object));
      frames.forEach((object) => newCanvas.remove(object));

      let documentStamps = pageSizesCopy
        .filter(({ shouldExport }) => shouldExport)
        .map((page) => {
          let image = newCanvas.toDataURL({
            top: page.top,
            format: 'png',
            width: page.width,
            height: page.height,
          });
          return {
            page: page.index + 1,
            image_base64: image,
            x_percent: 0.0,
            y_percent: 0.0,
            width_percent: 1.0,
          };
        });

      const result = await dispatch(api.Documents.stampDocument({ documentId, documentStamps }), {
        title: 'Fill & Sign',
        successText: 'Document has been updated successfully!',
      });
      setSaving(false);
      if (result && !!props.onSign) props.onSign();
    });
  }

  function onLoad(canvas, pageSizes, width, height) {
    setExportData({ canvas, pageSizes, width, height });
  }

  function onReducePages(number) {
    setMaxPages(Math.abs(parseInt(number)));
  }

  return (
    <div className="sign-document">
      <div
        className="sign-document-preview-screen"
        ref={(ref) => {
          if (ref) {
            setWidth(ref.clientWidth);
          }
        }}
      >
        {!url || (physician && !signatureData) ? (
          <ProgressMessage className="loading" size={70} />
        ) : null}
        {width && url && (!physician || signatureData) && (
          <FabricCanvas
            signature={signatureData}
            url={url}
            width={width}
            physician={physician}
            clinic={clinic}
            patient={patient}
            onLoad={onLoad}
            macroData={macroData}
            maxPages={maxPages}
            setTotalPages={setTotalPages}
            key={maxPages}
          />
        )}
      </div>
      <div className="sign-document-main-screen">
        <div className="button-wrapper">
          <div>
            {totalPages > 5 && !saving && (
              <ConfirmationButton
                name="Reduce # of pages"
                icon="gauge-max"
                onClick={(e, form) => onReducePages(form['Number of pages'])}
                style={{ width: '180px', margin: '0 10px' }}
                confirmation={{
                  message: 'How many pages do you need to edit (starting from the first)?',
                  form: [{ type: 'textField', label: 'Number of pages' }],
                }}
              />
            )}
          </div>
          {onClose && (
            <div>
              <DefaultButton
                name="Cancel"
                onClick={onClose}
                disabled={saving}
                style={{ width: '250px', margin: '0 10px' }}
              />
            </div>
          )}
          <div>
            <DefaultButton
              name="Save Changes"
              onClick={saveChanges}
              className="blue"
              loading={saving}
              style={{ width: '250px', margin: '0 10px' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const getGenericControls = (ids, options) => {
  let result = {};
  ids.forEach((id) => {
    result[id] = new fabric.Control(options);
  });
  return result;
};

const defaultControlsParams = {
  offsetY: -20,
  cursorStyle: 'pointer',
  sizeX: 30,
  sizeY: 30,
};

const tools = {
  textbox: {
    id: 'textbox',
    icon: <Icon src="text" height="24px" width="24px" alt="Textbox" showTooltip={true} />,
    label: 'Textbox',
    getObject: (options, state, setState) => {
      options = {
        ...options,
        fontSize: state.fontSize,
        lineHeight: 1,
        top: options.top - 10,
        width: 60,
        splitByGrapheme: true,
        dynamicMinWidth: 1000,
        fontFamily: 'Courier',
      };
      return new fabric.Textbox('', options);
    },
    afterAddedObject: (canvas, object) => {
      object.enterEditing();
      object.setSelectionStart(0);
    },
    canvasType: 'textbox',
    getControls: () => ({
      ...fabric.Textbox.prototype.controls,
      ...getGenericControls(['mtr', 'mb', 'mt', 'br', 'bl', 'tl', 'tr'], {
        visible: false,
      }),
      textIncrease: new fabric.Control({
        x: 0,
        y: -0.5,
        ...defaultControlsParams,
        mouseUpHandler: actionIncreaseFontSize,
        render: renderIcon(fontUpI),
      }),
      textDecrease: new fabric.Control({
        x: -0.5,
        y: -0.5,
        ...defaultControlsParams,
        mouseUpHandler: actionDecreaseFontSize,
        render: renderIcon(fontDownI),
      }),
      delete: new fabric.Control({
        x: 0.5,
        y: -0.5,
        ...defaultControlsParams,
        mouseUpHandler: actionDelete,
        render: renderIcon(deleteI),
      }),
    }),
  },
  x: {
    id: 'x',
    icon: <Icon src="xmark" height="24px" width="24px" alt="X" showTooltip={true} />,
    label: 'X',
    canvasType: 'i-text',
    getObject: (options) => {
      options = {
        ...options,
        top: options.top - 12,
        left: options.left - 6,
        fontSize: 20,
        fontWeight: 'bold',
        fontFamily: 'Helvetica',
        editable: false,
      };
      return new fabric.IText('x', options);
    },
    getControls: () => ({
      ...fabric.IText.prototype.controls,
      ...getGenericControls(['mtr', 'mb', 'mt', 'ml', 'mr'], {
        visible: false,
      }),
      delete: new fabric.Control({
        x: 0,
        y: -0.5,
        ...defaultControlsParams,
        mouseUpHandler: actionDelete,
        render: renderIcon(deleteI),
      }),
    }),
  },
  line: {
    id: 'line',
    icon: <Icon src="scribble" height="24px" width="24px" alt="Draw" showTooltip={true} />,
    label: 'Line',
    onSelect: (state, setState) => {
      const { canvas } = state;
      canvas.isDrawingMode = true;
      canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
      canvas.freeDrawingBrush.width = 5;
    },
    getObject: () => {},
  },
  eraser: {
    id: 'eraser',
    label: 'Eraser',
    icon: <Icon src="eraser" height="24px" width="24px" alt="Eraser" showTooltip={true} />,
    onSelect: (state, setState) => {
      const { canvas } = state;
      canvas.isDrawingMode = true;
      canvas.freeDrawingBrush = new fabric.EraserBrush(canvas);
      canvas.freeDrawingBrush.width = 20;
    },
    getObject: () => {},
  },
  signature: {
    id: 'signature',
    label: 'Signature',
    icon: <Icon src="signature" height="24px" width="24px" alt="Signature" showTooltip={true} />,
    canvasType: 'image',
    allowed: (state) => !!state.signatureImage,
    getObject: (options, state) => {
      let { height } = state.signatureImage;

      const DESIRED_HEIGHT = 50;

      let scaleFactor = DESIRED_HEIGHT / height;

      options = {
        ...options,
        scaleX: scaleFactor,
        scaleY: scaleFactor,
        top: options.top - DESIRED_HEIGHT,
      };
      return new fabric.Image(state.signatureImage, options);
    },
    getControls: () => ({
      ...fabric.Image.prototype.controls,
      ...getGenericControls(['mb', 'mt', 'ml', 'mr'], { visible: false }),
    }),
  },
  patientStamp: {
    id: 'patientStamp',
    label: 'Patient Label',
    icon: <Icon src="tag" height="24px" width="24px" alt="Patient Label" showTooltip={true} />,
    canvasType: 'group',
    allowed: (state) => !!state.patient,
    getObject: (options, state) => {
      const { patient } = state;
      const stampConfig = {
        strokeWidth: 1,
        rx: 0,
        ry: 0,
        ...options,
      };
      let linesData = [
        PatientUtils.getName(patient, undefined, false),
        `${PatientUtils.getGender(patient)} ${PatientUtils.getBirthdate(
          patient,
          'DD/MM/YYYY',
          false,
        )} ${getHealthCardLabel()}#: ${PatientUtils.getOhipNumber(patient, 'N/A/')}`,
        patient.address?.line1,
        [
          patient.address?.line2,
          patient.address?.city,
          patient.address?.state,
          patient.address?.postcode,
        ]
          .filter((l) => l)
          .join(', '),
        `Phone#: ${PatientUtils.getContact(patient, false)}`,
      ].filter((l) => !!l);

      return createGenericStamp(options, stampConfig, linesData, false);
    },
    getControls: () => ({
      ...fabric.Image.prototype.controls,
      ...getGenericControls(['mb', 'mt', 'ml', 'mr'], { visible: false }),
    }),
  },
  practitionerStamp: {
    id: 'practitionerStamp',
    label: 'Practitioner Stamp',
    icon: (
      <Icon src="stamp" height="24px" width="24px" alt="Practitioner Stamp" showTooltip={true} />
    ),
    style: { marginRight: '20px' },
    canvasType: 'group',
    allowed: (state) => !!state.physician && !!state.clinic,
    getObject: (options, state) => {
      const { physician, clinic } = state;
      const stampConfig = {
        strokeWidth: 3,
        rx: 10,
        ry: 10,
        ...options,
      };
      let linesData = [
        StaffUtils.getName(physician),
        physician.billing_number
          ? `${getBillingNumberLabel()}# ${physician.billing_number}, ${getCollegeLabel(
              physician.physician_type,
            )}# ${physician.college_registration}`
          : '',
        clinic.name,
        clinic.address.line1,
        [clinic.address.line2, clinic.address.city, clinic.address.state, clinic.address.postcode]
          .filter((l) => l)
          .join(', '),
        `Tel# ${clinic.phone}, Fax# ${clinic.fax}`,
      ].filter((l) => !!l);

      return createGenericStamp(options, stampConfig, linesData);
    },
    getControls: () => ({
      ...fabric.Image.prototype.controls,
      ...getGenericControls(['mb', 'mt', 'ml', 'mr'], { visible: false }),
    }),
  },
  copy: {
    id: 'copy',
    label: 'Copy',
    icon: <Icon src="copy" height="24px" width="24px" alt="Copy" showTooltip={true} />,
    onSelect: (state, setState) => {
      state.canvas.getActiveObject().clone(function (cloned) {
        setState((prevState) => ({ ...prevState, clipboard: cloned }));
      });
    },
  },
  paste: {
    id: 'paste',
    label: 'Paste',
    icon: <Icon src="paste" height="24px" width="24px" alt="Paste" showTooltip={true} />,
    instant: true,
    style: { marginRight: '20px' },
    onSelect: (state, setState) => {
      const { clipboard, canvas } = state;
      clipboard.clone(function (clonedObj) {
        canvas.discardActiveObject();
        clonedObj.set({
          left: clonedObj.left + 10,
          top: clonedObj.top + 10,
          evented: true,
        });
        if (clonedObj.type === 'activeSelection') {
          clonedObj.canvas = canvas;
          clonedObj.forEachObject(function (obj) {
            canvas.add(obj);
          });
          clonedObj.setCoords();
        } else {
          canvas.add(clonedObj);
        }
        clipboard.top += 10;
        clipboard.left += 10;
        canvas.setActiveObject(clonedObj);
      });
    },
  },
  delete: {
    id: 'delete',
    icon: <Icon src="trash" height="24px" width="24px" alt="Delete" showTooltip={true} />,
    label: 'Delete',
    instant: true,
    style: { marginRight: '20px' },
    onSelect: (state, setState) => {
      const { canvas } = state;
      let target = canvas.getActiveObject();
      if (target) {
        if (target.type === 'activeSelection') {
          // active selection needs a reference to the canvas.
          target.forEachObject(function (obj) {
            canvas.remove(obj);
          });
        } else {
          canvas.remove(target);
        }
        canvas.discardActiveObject();
      }
    },
  },
  undo: {
    id: 'undo',
    icon: <Icon src="undo" height="24px" width="24px" alt="Undo" showTooltip={true} />,
    label: 'Undo',
    onSelect: (state, setState) => {
      state.canvas.undo();
    },
  },
  redo: {
    id: 'redo',
    icon: <Icon src="redo" height="24px" width="24px" alt="Redo" showTooltip={true} />,
    style: { marginRight: '20px' },
    label: 'Redo',
    onSelect: (state, setState) => {
      state.canvas.redo();
    },
  },
  zoomOut: {
    id: 'zoomOut',
    allowed: (state) => !!state.allowZoom,
    icon: (
      <Icon
        src="magnifying-glass-minus"
        height="24px"
        width="24px"
        alt="Zoom Out"
        showTooltip={true}
      />
    ),
    label: 'Zoom Out',
    onSelect: (state, setState) => {
      setState((prevState) => ({ ...prevState, zoom: prevState.zoom * 0.9 }));
    },
  },
  zoomIn: {
    id: 'zoomIn',
    allowed: (state) => !!state.allowZoom,
    icon: (
      <Icon
        src="magnifying-glass-plus"
        height="24px"
        width="24px"
        alt="Zoom In"
        showTooltip={true}
      />
    ),
    label: 'Zoom In',
    onSelect: (state, setState) => {
      setState((prevState) => ({ ...prevState, zoom: prevState.zoom * 1.1 }));
    },
  },
};

let toolKey = 1;

const deleteI = document.createElement('img');
deleteI.src = deleteSvg;

const fontUpI = document.createElement('img');
fontUpI.src = fontIncreaseSvg;

const fontDownI = document.createElement('img');
fontDownI.src = fontDecreaseSvg;

const PDF_SCALE = 1.5;

const Base64Prefix = 'data:application/octet-stream;base64,';

export const FabricCanvas = ({
  signature,
  url,
  width,
  physician,
  patient,
  clinic,
  onLoad,
  macroData,
  pageNumber,
  maxPages,
  setTotalPages,
  allowZoom = true,
  pdfScale = PDF_SCALE,
}) => {
  const [canvasState, setCanvasState] = useState({
    canvas: undefined,
    zoom: 1,
    fontSize: 20,
    signatureImage: undefined,
    patient,
    physician,
    clinic,
    clipboard: undefined,
    tool: tools.textbox.id,
    allowZoom,
  });

  useEffect(() => {
    if (signature) {
      const signatureImage = document.createElement('img');
      signatureImage.src = signature;
      setCanvasState((prevState) => ({ ...prevState, signatureImage }));
    }
  }, [signature]);

  useEffect(() => {
    if (url) {
      MiscUtils.urlToBlob(url).then(async (blob) => {
        await setupCanvas(blob, pageNumber, maxPages);
      });
    }

    return () => {
      const textareas = document.querySelectorAll('textarea[data-fabric-hiddentextarea]');
      textareas.forEach((textarea) => {
        textarea.parentNode.removeChild(textarea);
      });
    };
  }, [url, pageNumber, maxPages]);

  useEffect(() => {
    const canvas = canvasState.canvas;

    function onKeyDown(event) {
      if (event.keyCode === 27) {
        if (canvas) {
          let target = canvas.getActiveObject();
          if (target) {
            canvas.discardActiveObject();
          } else {
            setCanvasState((prevState) => ({ ...prevState, tool: undefined }));
          }
        }
      } else if (
        (event.ctrlKey || event.metaKey) &&
        (event.keyCode === 89 || (event.shiftKey && event.keyCode === 90))
      ) {
        canvas.redo();
      } else if ((event.ctrlKey || event.metaKey) && event.keyCode === 90 && !event.shiftKey) {
        canvas.undo();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [canvasState.canvas]);

  useEffect(() => {
    if (canvasState.canvas) {
      canvasState.canvas.setZoom(canvasState.zoom);
    }
  }, [canvasState.canvas, canvasState.zoom]);

  useEffect(() => {
    const { canvas } = canvasState;

    if (canvas) {
      toolKey = toolKey + 1;
      let currentKey = toolKey;

      const addAllControls = () => {
        canvas.getObjects().forEach(function (object) {
          let newControls = getControls(object);
          if (newControls) object.controls = newControls;
        });
      };

      canvas.on('text:changed', function (opt) {
        const textbox = opt.target;

        let originalText = textbox.text;
        let newText = CPPUtils.checkMacro(originalText, macroData);

        let selectionStart = textbox.selectionStart;

        let longestLine = newText
          .split('\n')
          .map((l) => l.length)
          .reduce((a, b) => Math.max(a, b), 0);
        let width = Math.min(longestLine * textbox.fontSize * 0.65, canvasState.width);
        textbox.set('width', width);

        if (originalText !== newText) {
          textbox.set('text', newText);
          //this is necessary to reset the "undo" stack of textboxes
          textbox.exitEditing();
          textbox.enterEditing();
          let lengthDifference = newText.length - originalText.length;
          selectionStart += lengthDifference;
          textbox.setSelectionStart(selectionStart);
          textbox.setSelectionEnd(selectionStart);
        }
      });

      canvas.on('object:modified', function (event) {
        const { target } = event;
        if (target && target.fontSize) {
          setCanvasState((prevState) => ({
            ...prevState,
            fontSize: target.fontSize,
          }));
        }
      });

      canvas.on('history:undo', function (event) {
        addAllControls();
      });

      canvas.on('history:redo', function (event) {
        addAllControls();
      });

      canvas.on('object:added', function (event) {
        const { target } = event;
        if (target?.path) {
          target.selectable = false;
          target.erasable = true;
        }
      });

      canvas.on('mouse:down', function (event) {
        if (currentKey === toolKey) {
          const { target } = event;

          if (target?.selectable || target?.text) {
          } else {
            const pointer = canvas.getPointer(event.e);
            addTool(canvas, pointer.x, pointer.y, canvasState, setCanvasState);
          }
        }
      });
    }
  }, [canvasState, macroData]);

  useEffect(() => {
    const canvas = canvasState.canvas;
    if (canvas && !canvasState.tool) {
      canvas.isDrawingMode = false;
      canvas.freeDrawingBrush = null;
    }
  }, [canvasState.tool, canvasState.canvas]);

  async function setupCanvas(blob, pageNumber, maxPages) {
    let pdfPages = await printPDF(blob, undefined, pdfScale);
    setTotalPages(pdfPages.length);

    if (maxPages > 0) {
      pdfPages = pdfPages.slice(0, maxPages);
    }

    if (pageNumber !== undefined) {
      pdfPages = [pdfPages[pageNumber]];
    }

    let result = await Promise.all(pdfPages);

    let pageWidth = 0;
    let pageHeight = 0;

    const numberPages = maxPages || result.length;

    for (let i = 0; i < numberPages; i++) {
      let page = result[i].page;
      let view = page.view.map((v) => v * pdfScale);
      pageWidth = Math.max(pageWidth, view[2]);
      pageHeight += view[3];
    }

    let canvasWidth = Math.max(width, pageWidth) * pdfScale;
    let canvasHeight = pageHeight;

    if (pageNumber !== undefined) {
      canvasWidth = width;
      const ratio = pageHeight / pageWidth;
      canvasHeight = canvasWidth * ratio;
    }

    let mainCanvas = new fabric.Canvas('canvas', {
      height: canvasHeight,
      width: canvasWidth,
      renderOnAddRemove: true,
      objectCaching: true,
      statefulCache: true,
    });
    let currentY = 0;
    const pageSizes = [];

    let pageObjects = [];
    let borderObjects = [];

    for (let i = 0; i < numberPages; i++) {
      let canvas = result[i].canvas;
      let view = result[i].page.view.map((v) => v * pdfScale);
      let options = {
        left: view[0],
        top: currentY,
        width: pageNumber === undefined ? view[2] : canvasWidth,
        height: pageNumber === undefined ? view[3] : canvasHeight,
        cursorStyle: 'pointer',
        selectable: false,
        evented: false,
        erasable: false,
      };
      pageSizes.push({
        width: options.width,
        height: options.height,
      });
      let object = new fabric.Image(canvas, options);
      pageObjects.push(object);

      currentY += view[3];

      let border = new fabric.Rect({
        ...options,
        fill: 'transparent',
        stroke: 'black',
        strokeWidth: 1,
      });
      borderObjects.push(border);
    }

    pageObjects.forEach((object) => mainCanvas.add(object));

    if (pageNumber === undefined) borderObjects.forEach((object) => mainCanvas.add(object));

    mainCanvas.clearHistory();
    setCanvasState((prevState) => ({
      ...prevState,
      canvas: mainCanvas,
      width: pageWidth,
    }));
    onLoad(mainCanvas, pageSizes, pageWidth, pageHeight);
  }

  return (
    <div className="canvas-wrapper">
      <div className="tools">
        {Object.values(tools).map((t) => {
          let classname = 'tool-button';
          if (canvasState.tool === t.id) classname += ' active';
          if (t.allowed && !t.allowed(canvasState)) return null;
          return (
            <div
              key={t.id}
              className={classname}
              style={t.style}
              onClick={() => {
                if (t.onSelect) {
                  t.onSelect(canvasState, setCanvasState);
                }
                if (t.getObject) {
                  setCanvasState((prevState) => ({
                    ...prevState,
                    tool: prevState.tool === t.id ? null : t.id,
                  }));
                }
              }}
            >
              {t.icon || t.label}
            </div>
          );
        })}
        <DefaultTooltip
          placement="top"
          title={<HealthRecordsShortcuts showHelp={false} titleStyle={{ color: 'white' }} />}
          arrow={true}
        >
          <div className="tool-button">
            <Icon src="circle-info" height="24px" width="24px" showTooltip={false} />
          </div>
        </DefaultTooltip>
      </div>
      <div className="canvas-scroller">
        <canvas id="canvas" tabIndex="1" />
      </div>
    </div>
  );
};

async function printPDF(pdfData, pages, pdfScale) {
  pdfData = pdfData instanceof Blob ? await MiscUtils.readBlobWithFileReader(pdfData) : pdfData;
  const data = atob(
    pdfData.startsWith(Base64Prefix) ? pdfData.substring(Base64Prefix.length) : pdfData,
  );
  // Using DocumentInitParameters object to load binary data.
  const loadingTask = pdfjs.getDocument({ data });
  return loadingTask.promise.then((pdf) => {
    const numPages = pdf.numPages;
    return new Array(numPages).fill(0).map((__, i) => {
      const pageNumber = i + 1;
      if (pages && pages.indexOf(pageNumber) === -1) {
        return;
      }
      return pdf.getPage(pageNumber).then((page) => {
        //  retina scaling
        const viewport = page.getViewport({ scale: pdfScale });
        // Prepare canvas using PDF page dimensions
        const canvas = document.createElement('canvas');
        const canvasContext = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        // Render PDF page into canvas context
        const renderContext = {
          canvasContext,
          viewport,
        };
        const renderTask = page.render(renderContext);
        return renderTask.promise.then(() => {
          return { canvas, page };
        });
      });
    });
  });
}

function getControls(object) {
  const type = object.get('type');
  const matchingTool = Object.values(tools).find((t) => t.canvasType === type);
  if (matchingTool) return matchingTool.getControls();
}

function createGenericStamp(options, stampConfig, linesData, outerBorder = true) {
  const LINE_HEIGHT = 20;
  const PADDING = 15;
  const CHAR_WIDTH = 10;

  //find max character count in lines:
  const max = linesData.reduce((max, line) => Math.max(max, line.length), 0);

  const textConfig = {
    fill: 'black',
    fontFamily: 'Courier',
    fontSize: 15,
    selectable: false,
    ...options,
  };

  // create group
  const stampGroup = new fabric.Group([], {
    originX: 'center',
    originY: 'center',
    left: options.left || 50,
    top: options.top || 50,
    ...options,
  });

  const rectangleStamp = new fabric.Rect({
    fill: 'rgba(255, 255, 255, 0.7)',
    stroke: '#000',
    strokeWidth: 1,
    ...stampConfig,
    width: max * CHAR_WIDTH,
    height: linesData.length * LINE_HEIGHT + PADDING * 2,
  });

  const rectangleStampOuter = new fabric.Rect({
    fill: 'rgba(255, 255, 255, 0.7)',
    stroke: '#000',
    strokeWidth: 1,
    ...stampConfig,
    height: rectangleStamp.height + PADDING,
    width: rectangleStamp.width + PADDING,
    left: rectangleStamp.left - PADDING / 2,
    top: rectangleStamp.top - PADDING / 2,
    rx: (rectangleStamp.rx || 0) + 5,
    ry: (rectangleStamp.ry || 0) + 5,
  });

  if (outerBorder) stampGroup.addWithUpdate(rectangleStampOuter);
  stampGroup.addWithUpdate(rectangleStamp);

  const getLine = (text, index) => {
    return new fabric.Text(text, {
      ...textConfig,
      top: index * LINE_HEIGHT + PADDING - rectangleStamp.height / 2,
      left: 0,
      originX: 'center',
    });
  };

  // Create and add text lines
  linesData.forEach((lineText, index) => {
    const textLine = getLine(lineText, index);
    stampGroup.add(textLine); // No need for 'addWithUpdate' here as we're recalculating after all items are added
  });

  // Ensure the group recalculates its boundaries
  stampGroup.addWithUpdate();

  return stampGroup;
}

function renderIcon(icon) {
  return (ctx, left, top, styleOverride, fabricObject) => {
    const size = 25;
    const padding = 1;
    const boxSize = size + padding * 2;
    ctx.save();
    ctx.translate(left, top);
    ctx.beginPath();
    ctx.fillStyle = 'white';
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 1;
    ctx.roundRect(-boxSize / 2, -boxSize / 2, boxSize, boxSize, 5);
    ctx.fill();
    ctx.stroke();
    ctx.drawImage(icon, -size / 2, -size / 2, size, size);
    ctx.restore();
  };
}

CanvasRenderingContext2D.prototype.roundRect = function (x, y, width, height, radius) {
  if (width < 2 * radius) radius = width / 2;
  if (height < 2 * radius) radius = height / 2;
  this.beginPath();
  this.moveTo(x + radius, y);
  this.arcTo(x + width, y, x + width, y + height, radius);
  this.arcTo(x + width, y + height, x, y + height, radius);
  this.arcTo(x, y + height, x, y, radius);
  this.arcTo(x, y, x + width, y, radius);
  this.closePath();
  return this;
};

function actionDelete(eventData, transform) {
  var target = transform.target;
  var canvas = target.canvas;
  canvas.remove(target);
}

function actionIncreaseFontSize(eventData, transform) {
  actionChangeFont(eventData, transform, 2);
}

function actionDecreaseFontSize(eventData, transform) {
  actionChangeFont(eventData, transform, -2);
}

function actionChangeFont(eventData, transform, fontChange = 1) {
  var target = transform.target;
  var canvas = target.canvas;
  if (canvas) {
    let fontSize = target.get('fontSize') + fontChange;
    target.set('fontSize', fontSize);
    canvas.fire('object:modified', { target });
    canvas.renderAll();
  }
}

function addTool(canvas, left, top, canvasState, setCanvasState) {
  let object;
  let options = { top, left, erasable: false };

  const tool = tools[canvasState.tool];

  object = tool?.getObject(options, canvasState, setCanvasState);

  if (object) {
    let newControls = getControls(object);
    if (newControls) object.controls = newControls;
    canvas.isDrawingMode = false;
    canvas.add(object);
    canvas.setActiveObject(object);
    if (tool?.afterAddedObject) tool?.afterAddedObject(canvas, object);
  }
}

SignDocument.propTypes = {
  document: PropTypes.object.isRequired,
  physician: PropTypes.object,
  patient: PropTypes.object,
  onClose: PropTypes.func,
  onSign: PropTypes.func,
  url: PropTypes.string,
};

export default SignDocument;
